/**
 * ArkFloorPlan 地面素材库（2026-10-18）—— 画布底纹 / 地面素材 / 区域填充的唯一图形来源。
 *
 * 档位名（token）由 C# FloorPlanBackdropExtensions.ToCssToken 给出：
 *   简约：dots 点阵 / grid 方格 / plate 沉底板+点阵（2026-10-04 的门店级底纹）
 *   室内：wood 直拼木地板 / herring 人字拼 / tile 方砖 / hex 六角砖 / tatami 榻榻米 / terrazzo 水磨石 /
 *         marble 大理石 / carpet 地毯 / concrete 微水泥
 *   户外：deck 户外木平台 / pavers 石板 / lawn 草坪
 *   none / 未知 → 不铺
 *
 * 这里只管几何：图元只挂类名（ark-mt-a…），颜色由 ark-floor-plan.css 的「地面素材」段按 [data-mt] 给 CSS 变量，
 * 浅深两套主题各一组 —— 切主题不用重建。随机图元（水磨石碎石、微水泥麻点、草簇、石板长度）用固定种子，所有终端画出同一张图。
 *
 * 约束（见 客户端界面/设计方案.md §三.4）：
 *   - 素材只能是「地」：地图空台白底无描边、只靠投影，素材平均亮度不得高于画布 #f3f4f6（由 FloorPlanBackdropTests 从 CSS 守）；
 *   - 不用 SVG 滤镜（v3.36 缩放写回时整层重新栅格化，feTurbulence 之类在安卓低端机上整幅重算）；
 *   - 线宽 ≥ 1.5（全图档约 45% 时不消失）。
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(tag, attrs, cls) {
    const e = document.createElementNS(SVG_NS, tag);
    if (attrs) for (const k in attrs) e.setAttribute(k, String(attrs[k]));
    if (cls) e.setAttribute('class', cls);
    return e;
}

const f2 = n => Math.round(n * 100) / 100;

/** 固定种子随机数（mulberry32） */
function rng(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** 图元跨周期边界时在相邻周期补画，保证无缝 */
function wrapOffsets(tw, th) {
    const o = [];
    for (const dx of [-tw, 0, tw]) for (const dy of [-th, 0, th]) o.push([dx, dy]);
    return o;
}
const inTile = (x, y, tw, th, pad) => x > -pad && x < tw + pad && y > -pad && y < th + pad;

const dotsPaint = (p) => { p.appendChild(el('circle', { cx: 20, cy: 20, r: 1.6 }, 'ark-mt-ink')); };

/**
 * 素材描述表：tile = 图案周期（画布像素），rot = 斜铺角度，paint(pattern) 往一个周期里画图元，
 * under(g, w, h) 可选 —— 铺在图案下面的整块（沉底板）。
 */
const MATERIALS = {
    dots: { tile: [40, 40], paint: dotsPaint },

    grid: {
        tile: [80, 80],
        paint: (p) => {
            p.appendChild(el('path', { d: 'M0 0V80M20 0V80M40 0V80M60 0V80M0 0H80M0 20H80M0 40H80M0 60H80', fill: 'none' }, 'ark-mt-s'));
            p.appendChild(el('path', { d: 'M0 0H80M0 0V80', fill: 'none' }, 'ark-mt-s2'));
        },
    },

    plate: {
        tile: [40, 40],
        paint: dotsPaint,
        under: (g, w, h) => { g.appendChild(el('rect', { x: 0.5, y: 0.5, width: w - 1, height: h - 1, rx: 2 }, 'ark-mt-b ark-mt-s')); },
    },

    wood: {
        tile: [240, 120],
        paint: (p) => {
            p.appendChild(el('rect', { width: 240, height: 60 }, 'ark-mt-a'));
            p.appendChild(el('rect', { y: 60, width: 120, height: 60 }, 'ark-mt-b'));
            p.appendChild(el('rect', { x: 120, y: 60, width: 120, height: 60 }, 'ark-mt-a'));
            p.appendChild(el('path', { d: 'M0 0.5H240M0 60.5H240M0.5 0V60M120.5 60V120', fill: 'none', 'stroke-width': 1.2 }, 'ark-mt-s'));
        },
    },

    // 人字拼：轴对齐 staircase 排列（板 w×L，L = 4w），周期 2L×2L，再整体转 45°
    herring: {
        tile: [160, 160], rot: 45,
        paint: (p) => {
            const w = 20, L = 80, T = 160, rs = [];
            for (let j = -2; j <= 2; j++) {
                for (let k = -9; k <= 9; k++) {
                    const hx = k * w, hy = k * w + j * T, vy = (k + 1) * w + j * T;
                    if (hx < T && hx + L > 0 && hy < T && hy + w > 0) rs.push([hx, hy, L, w, 'ark-mt-a']);
                    if (hx < T && hx + w > 0 && vy < T && vy + L > 0) rs.push([hx, vy, w, L, 'ark-mt-b']);
                }
            }
            // 先铺满板面再统一描缝：描边在后，相邻板不会盖掉半条缝
            for (const r of rs) p.appendChild(el('rect', { x: r[0], y: r[1], width: r[2], height: r[3] }, r[4]));
            for (const r of rs) p.appendChild(el('rect', { x: r[0], y: r[1], width: r[2], height: r[3], fill: 'none', 'stroke-width': 1.5 }, 'ark-mt-s'));
        },
    },

    // 方砖：60px（刻意不用 80，免得和编辑态 80px 网格重合），四块一组微调明度
    tile: {
        tile: [120, 120],
        paint: (p) => {
            p.appendChild(el('rect', { width: 60, height: 60 }, 'ark-mt-a'));
            p.appendChild(el('rect', { x: 60, width: 60, height: 60 }, 'ark-mt-b'));
            p.appendChild(el('rect', { y: 60, width: 60, height: 60 }, 'ark-mt-c'));
            p.appendChild(el('rect', { x: 60, y: 60, width: 60, height: 60 }, 'ark-mt-d'));
            // 周期边界两侧各画半条缝，拼起来与内部缝同宽
            p.appendChild(el('path', { d: 'M0 0V120M60 0V120M120 0V120M0 0H120M0 60H120M0 120H120', fill: 'none', 'stroke-width': 1.5 }, 'ark-mt-s'));
        },
    },

    // 六角砖：尖顶，边长 26，横向周期取整 45（真值 45.03）
    hex: {
        tile: [45, 78],
        paint: (p) => {
            const hx = (x, y) => `M${x} ${y - 26}L${x + 22.5} ${y - 13}L${x + 22.5} ${y + 13}L${x} ${y + 26}L${x - 22.5} ${y + 13}L${x - 22.5} ${y - 13}Z`;
            const d = [[0, 0], [45, 0], [0, 78], [45, 78], [22.5, 39], [-22.5, 39], [67.5, 39]].map(c => hx(c[0], c[1])).join('');
            p.appendChild(el('rect', { width: 45, height: 78 }, 'ark-mt-a'));
            p.appendChild(el('path', { d, fill: 'none', 'stroke-width': 1.5, 'stroke-linejoin': 'round' }, 'ark-mt-s'));
        },
    },

    tatami: {
        tile: [320, 160],
        paint: (p) => {
            const mats = [[0, 0], [160, 0], [-80, 80], [80, 80], [240, 80]];
            for (const [x, y] of mats) {
                p.appendChild(el('rect', { x, y, width: 160, height: 80 }, 'ark-mt-a'));
                p.appendChild(el('rect', { x, y, width: 160, height: 7 }, 'ark-mt-b'));
                p.appendChild(el('rect', { x, y: y + 73, width: 160, height: 7 }, 'ark-mt-b'));
            }
            for (const [x, y] of mats) p.appendChild(el('rect', { x, y, width: 160, height: 80, fill: 'none', 'stroke-width': 1.5 }, 'ark-mt-s'));
        },
    },

    terrazzo: {
        tile: [200, 200],
        paint: (p) => {
            const r = rng(7);
            p.appendChild(el('rect', { width: 200, height: 200 }, 'ark-mt-a'));
            for (let i = 0; i < 52; i++) {
                const x = r() * 200, y = r() * 200, rad = 1.6 + r() * 3.4, n = 4 + Math.floor(r() * 3), a0 = r() * 6.283, pk = r();
                const cls = pk < 0.42 ? 'ark-mt-c0' : pk < 0.72 ? 'ark-mt-c1' : pk < 0.9 ? 'ark-mt-c2' : 'ark-mt-c3';
                const pts = [];
                for (let k = 0; k < n; k++) {
                    const a = a0 + k * 6.283 / n + (r() - 0.5) * 0.6, rr = rad * (0.7 + r() * 0.5);
                    pts.push([x + Math.cos(a) * rr, y + Math.sin(a) * rr]);
                }
                for (const [dx, dy] of wrapOffsets(200, 200)) {
                    if (!inTile(x + dx, y + dy, 200, 200, 8)) continue;
                    p.appendChild(el('path', { d: 'M' + pts.map(q => f2(q[0] + dx) + ' ' + f2(q[1] + dy)).join('L') + 'Z' }, cls));
                }
            }
        },
    },

    // 大理石：每道纹理是周期 480 的正弦叠加，横向天然无缝；纵向补画上下两份
    marble: {
        tile: [480, 480], rot: -28,
        paint: (p) => {
            const T = 480;
            const vein = (y0, a, ph) => {
                let d = '';
                for (let x = 0; x <= T; x += 6) {
                    let y = y0;
                    a.forEach((amp, i) => { y += amp * Math.sin(2 * Math.PI * (i + 1) * x / T + ph[i]); });
                    d += (x ? 'L' : 'M') + x + ' ' + f2(y);
                }
                return d;
            };
            const veins = [
                { y: 110, a: [30, 12, 6, 3, 2], p: [0.3, 1.9, 4.1, 2.2, 5], w: 2.4, cls: 'ark-mt-s' },
                { y: 300, a: [42, 9, 7, 3, 1.5], p: [2.1, 0.4, 3.3, 1.2, 4.4], w: 1.4, cls: 'ark-mt-s2' },
                { y: 400, a: [16, 20, 5, 2.5, 2], p: [4.4, 2.7, 0.9, 3.8, 0.2], w: 1.5, cls: 'ark-mt-s3' },
            ];
            p.appendChild(el('rect', { width: T, height: T }, 'ark-mt-a'));
            for (const v of veins) {
                const d = vein(v.y, v.a, v.p);
                for (const dy of [-T, 0, T]) {
                    p.appendChild(el('path', { d, transform: `translate(0 ${dy})`, fill: 'none', 'stroke-width': v.w, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, v.cls));
                }
            }
        },
    },

    carpet: {
        tile: [48, 48],
        paint: (p) => {
            p.appendChild(el('rect', { width: 48, height: 48 }, 'ark-mt-a'));
            p.appendChild(el('path', { d: 'M0 24L24 0L48 24L24 48Z', fill: 'none', 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }, 'ark-mt-s'));
            for (const [cx, cy] of [[24, 24], [0, 0], [48, 0], [0, 48], [48, 48]]) p.appendChild(el('circle', { cx, cy, r: 1.8 }, 'ark-mt-c0'));
        },
    },

    concrete: {
        tile: [240, 240],
        paint: (p) => {
            const r = rng(11);
            p.appendChild(el('rect', { width: 240, height: 240 }, 'ark-mt-a'));
            for (let i = 0; i < 8; i++) {
                const x = r() * 240, y = r() * 240, rad = 28 + r() * 44, cls = i % 2 ? 'ark-mt-b' : 'ark-mt-c';
                for (const [dx, dy] of wrapOffsets(240, 240)) {
                    if (inTile(x + dx, y + dy, 240, 240, rad)) p.appendChild(el('circle', { cx: f2(x + dx), cy: f2(y + dy), r: f2(rad), 'fill-opacity': 0.55 }, cls));
                }
            }
            for (let i = 0; i < 170; i++) {
                const x = r() * 240, y = r() * 240, rad = 0.7 + r() * 0.9, cls = r() < 0.6 ? 'ark-mt-c0' : 'ark-mt-c1';
                for (const [dx, dy] of wrapOffsets(240, 240)) {
                    if (inTile(x + dx, y + dy, 240, 240, 2)) p.appendChild(el('circle', { cx: f2(x + dx), cy: f2(y + dy), r: f2(rad) }, cls));
                }
            }
        },
    },

    deck: {
        tile: [480, 144],
        paint: (p) => {
            p.appendChild(el('rect', { width: 480, height: 144 }, 'ark-mt-c'));
            const offs = [0, 160, 80, 240];
            for (let i = 0; i < 4; i++) {
                const y = i * 36, cls = i % 2 ? 'ark-mt-b' : 'ark-mt-a';
                for (let x = offs[i] - 240; x < 480; x += 240) p.appendChild(el('rect', { x: x + 1.5, y, width: 237, height: 32 }, cls));
            }
        },
    },

    pavers: {
        tile: [480, 224],
        paint: (p) => {
            const r = rng(23);
            p.appendChild(el('rect', { width: 480, height: 224 }, 'ark-mt-c'));
            for (let i = 0; i < 4; i++) {
                const y = i * 56, lens = [];
                let sum = 0;
                while (sum < 480) {
                    let l = [80, 96, 112, 128, 144][Math.floor(r() * 5)];
                    if (480 - sum - l < 64) l = 480 - sum;
                    lens.push(l);
                    sum += l;
                }
                let x = Math.floor(r() * 480);
                for (const l of lens) {
                    const cls = 'ark-mt-c' + Math.floor(r() * 4);
                    for (const dx of [-480, 0]) p.appendChild(el('rect', { x: x + dx + 1, y: y + 1, width: l - 2, height: 54, rx: 3 }, cls));
                    x += l;
                }
            }
        },
    },

    lawn: {
        tile: [160, 160],
        paint: (p) => {
            const r = rng(5);
            let d = '';
            for (let i = 0; i < 40; i++) {
                const x = r() * 160, y = r() * 160, h = 5 + r() * 4;
                const segs = [[-0.45, 0.8], [0, 1], [0.45, 0.85]].map(([a, k]) => {
                    const ang = a + (r() - 0.5) * 0.3;
                    return [Math.sin(ang) * h * k, -Math.cos(ang) * h * k];
                });
                for (const [dx, dy] of wrapOffsets(160, 160)) {
                    if (!inTile(x + dx, y + dy, 160, 160, 10)) continue;
                    for (const g of segs) d += `M${f2(x + dx)} ${f2(y + dy)}l${f2(g[0])} ${f2(g[1])}`;
                }
            }
            p.appendChild(el('rect', { width: 160, height: 160 }, 'ark-mt-a'));
            p.appendChild(el('path', { d, fill: 'none', 'stroke-width': 1.6, 'stroke-linecap': 'round' }, 'ark-mt-s'));
        },
    },
};

/** 该档位有没有图形（none / 未知 → false） */
export function hasMaterial(token) {
    return !!(token && MATERIALS[token]);
}

/**
 * 建一个 <pattern>：data-mt 挂在 pattern 上，CSS 变量随 DOM 继承给周期内的图元。
 * scale 给缩略图用（图案按比例缩小，与画布缩放同一视觉）。
 */
export function createMaterialPattern(token, id, scale = 1) {
    const m = MATERIALS[token];
    if (!m) return null;
    const [tw, th] = m.tile;
    const transforms = [];
    if (scale !== 1) transforms.push(`scale(${scale})`);
    if (m.rot) transforms.push(`rotate(${m.rot})`);
    const p = el('pattern', { id, width: tw, height: th, patternUnits: 'userSpaceOnUse', 'data-mt': token });
    if (transforms.length) p.setAttribute('patternTransform', transforms.join(' '));
    m.paint(p);
    return p;
}

/**
 * 往 g 里铺一整块素材：可选的整块底（沉底板）+ 图案填充矩形。defs 由调用方提供（pattern 挂进去）。
 */
export function fillMaterial(g, defs, token, id, x, y, w, h, scale = 1) {
    const m = MATERIALS[token];
    if (!m) return false;
    const pattern = createMaterialPattern(token, id, scale);
    defs.appendChild(pattern);
    if (m.under) {
        const under = el('g', { transform: `translate(${x} ${y})`, 'data-mt': token });
        m.under(under, w, h);
        g.appendChild(under);
    }
    g.appendChild(el('rect', { x, y, width: w, height: h, fill: `url(#${id})` }));
    return true;
}

let _seq = 0;

/**
 * 画布底纹层：一个铺满画布的 svg（viewBox = 画布像素）。none / 未知档位返回 null。
 */
export function buildBackdropSvg(token, w, h, idPrefix = 'ark') {
    if (!hasMaterial(token)) return null;
    const svg = el('svg', { viewBox: `0 0 ${w} ${h}`, width: w, height: h, 'data-mt': token }, 'ark-floor-plan-backdrop');
    const defs = el('defs');
    svg.appendChild(defs);
    const g = el('g');
    svg.appendChild(g);
    fillMaterial(g, defs, token, `${idPrefix}-bd-${token}-${++_seq}`, 0, 0, w, h);
    return svg;
}

/**
 * Admin 素材缩略图 / 预览：找到 root 下所有 svg[data-mt-swatch]，整块内容由这里画（Blazor 只渲染空 svg，
 * 不往里放子节点 —— 外来节点插进 Blazor 管的子树会打乱它按下标做的增量更新）。
 *   data-mt-scale   图案缩放（缩略图默认 0.5，与画布全图档观感接近；预览框给 1）
 *   data-mt-samples "false" 时不画样例桌（预览框用真实磁贴样式叠在上面）
 * 同一档位已画过的跳过，可在每次渲染后无脑调用。
 */
export function paintSwatches(root) {
    if (!root) return;
    const list = root.querySelectorAll ? root.querySelectorAll('svg[data-mt-swatch]') : [];
    list.forEach(svg => {
        const token = svg.getAttribute('data-mt-swatch');
        if (svg.getAttribute('data-mt-painted') === token) return;
        while (svg.firstChild) svg.removeChild(svg.firstChild);
        const vb = (svg.getAttribute('viewBox') || '0 0 120 75').split(/[\s,]+/).map(Number);
        const w = vb[2] || 120, h = vb[3] || 75;
        const scale = Number(svg.getAttribute('data-mt-scale') || 0.5);
        const defs = el('defs');
        svg.appendChild(defs);
        svg.appendChild(el('rect', { width: w, height: h }, 'ark-mt-canvas'));
        fillMaterial(svg, defs, token, `ark-sw-${token}-${++_seq}`, 0, 0, w, h, scale);
        if (svg.getAttribute('data-mt-samples') !== 'false') {
            // 样例桌：白色空台（无描边、只靠投影，看它会不会被素材吞掉）+ 用餐中绿台；投影画成偏移的半透明底
            const tile = (x, y, size, cls) => {
                svg.appendChild(el('rect', { x, y: y + 1.2, width: size, height: size, rx: 4 }, 'ark-mt-sample-shadow'));
                svg.appendChild(el('rect', { x, y, width: size, height: size, rx: 4 }, cls));
            };
            tile(w * 0.58, h * 0.27, h * 0.43, 'ark-mt-sample-empty');
            tile(w * 0.27, h * 0.5, h * 0.35, 'ark-mt-sample-busy');
        }
        svg.setAttribute('data-mt-painted', token);
    });
}

export default { hasMaterial, createMaterialPattern, fillMaterial, buildBackdropSvg, paintSwatches };
